import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { sendEmail, buildVisitorEmail, buildOwnerEmail, OWNER_EMAIL } from '../services/emailService';
import projectsData from '../projects_dataset.json';
import logo from '../assets/ChatGPT Image Aug 10, 2026, 06_50_42 PM.png';

const GROQ_API_KEY = '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';
const GROQ_MODEL   = 'groq/compound-mini';
const GROQ_URL     = 'https://api.groq.com/openai/v1/chat/completions';

const CATEGORIES = projectsData.metadata.categories;
const LEVELS     = ['BE/B.Tech', 'M.Tech', 'PhD', 'Diploma', 'MCA', 'MBA', 'BSc'];

const CATEGORY_ICONS = {
  'Artificial Intelligence & Machine Learning': '🤖',
  'Internet of Things (IoT)': '📡',
  'Blockchain Technology': '⛓️',
  'Cybersecurity': '🔐',
  'Cloud Computing': '☁️',
  'Data Science & Big Data': '📊',
  'Robotics & Automation': '🦾',
  'Embedded Systems': '🔧',
  'VLSI & Chip Design': '💾',
  'Power Systems & Electrical Engineering': '⚡',
  'Renewable Energy & EV Systems': '🌱',
  'Computer Vision & Image Processing': '👁️',
  'Natural Language Processing': '💬',
  'Web & Mobile Application Development': '💻',
  'Networking & Communication Systems': '🌐',
  'Biomedical Engineering & Healthcare Tech': '🏥',
  'Civil & Structural Engineering': '🏗️',
  'Mechanical & Manufacturing Systems': '⚙️',
  'Augmented/Virtual Reality': '🥽',
  'Quantum Computing': '⚛️',
};

function getProjectSuggestions(category, level, count = 6) {
  const all = projectsData.projects.filter(
    (p) => p.category === category && (level ? p.level === level : true)
  );
  return all.sort(() => 0.5 - Math.random()).slice(0, count);
}

async function generateGroqReport({ title, techStack, category, level, description, name }) {
  const prompt = `You are a senior academic project advisor at Connect Students, India's top academic project company.

A student named ${name} has requested a project proposal for:
- Project Title: "${title}"
- Category: ${category}
- Academic Level: ${level}
- Tech Stack: ${Array.isArray(techStack) ? techStack.join(', ') : techStack || 'To be decided'}
- Description: ${description || 'Standard implementation'}

Generate a detailed but concise project proposal with these exact sections:

## Project Overview
2-3 sentences on what this project does and its real-world significance.

## How It Works
Step-by-step in 5 clear bullet points — simple language.

## System Architecture
List 4 key modules with one-line description each: Input Layer → Processing → AI/Logic Engine → Output/Dashboard.

## Technology Stack
Each technology with its specific role in this project.

## 15-Day Delivery Timeline
- Day 1–3: Requirements & environment setup
- Day 4–7: Core algorithm/model development
- Day 8–11: Integration & testing
- Day 12–14: Report writing & PPT creation
- Day 15: Demo video & final delivery

## What You Will Receive
List 7 deliverables.

## Research Paper Guidance
Target journals (IEEE/Springer/Elsevier), novelty angle, and 2-3 key publication tips.

## Why This Project Stands Out
3 compelling bullet points for evaluators.

Be specific, professional, and student-friendly.`;

  const res = await fetch(GROQ_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${GROQ_API_KEY}` },
    body: JSON.stringify({
      model: GROQ_MODEL,
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.7,
      max_tokens: 1400,
    }),
  });
  if (!res.ok) throw new Error('Groq API error');
  const data = await res.json();
  return data.choices[0].message.content;
}

// ─── Render rich markdown ──────────────────────────────────────────────────────
function ReportRenderer({ text }) {
  const lines = text.split('\n');
  return (
    <div className="space-y-1.5">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-2" />;
        if (line.startsWith('## ')) {
          return (
            <div key={i} className="flex items-center gap-2 mt-5 mb-2">
              <div className="w-1 h-5 rounded-full bg-royal-gold" />
              <h3 className="font-playfair font-bold text-royal-navy text-base">{line.replace('## ', '')}</h3>
            </div>
          );
        }
        if (line.startsWith('- ')) {
          const content = line.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          return (
            <div key={i} className="flex items-start gap-2 ml-3">
              <div className="w-1.5 h-1.5 rounded-full bg-royal-gold mt-2 flex-shrink-0" />
              <p className="font-inter text-sm text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: content }} />
            </div>
          );
        }
        const bold = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-royal-navy">$1</strong>');
        return <p key={i} className="font-inter text-sm text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: bold }} />;
      })}
    </div>
  );
}

// ─── Step indicator ────────────────────────────────────────────────────────────
function StepBar({ current, total, labels }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {labels.map((label, i) => (
        <React.Fragment key={i}>
          <div className="flex flex-col items-center gap-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
              i < current ? 'bg-royal-gold text-white' :
              i === current ? 'bg-royal-navy text-white ring-4 ring-royal-navy/20' :
              'bg-parchment-dark text-slate-royal/40'
            }`}>
              {i < current ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              ) : i + 1}
            </div>
            <span className={`text-[10px] font-inter font-medium hidden sm:block ${i === current ? 'text-royal-navy' : 'text-slate-royal/40'}`}>{label}</span>
          </div>
          {i < labels.length - 1 && (
            <div className={`h-0.5 w-8 sm:w-16 rounded-full transition-colors duration-500 ${i < current ? 'bg-royal-gold' : 'bg-parchment-dark'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

const STEPS = ['Intent', 'Domain', 'Level', 'Project', 'Details', 'Report'];

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function TalkToExpert({ isOpen, onClose }) {
  const [step, setStep]               = useState(0);
  const [hasIdea, setHasIdea]         = useState(null);
  const [category, setCategory]       = useState('');
  const [level, setLevel]             = useState('');
  const [projectIdea, setProjectIdea] = useState('');
  const [selProject, setSelProject]   = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [form, setForm]               = useState({ name:'', phone:'', email:'', college:'', description:'' });
  const [errors, setErrors]           = useState({});
  const [generating, setGenerating]   = useState(false);
  const [report, setReport]           = useState('');
  const [reportErr, setReportErr]     = useState('');

  const goNext = () => setStep(s => s + 1);
  const goBack = () => setStep(s => s - 1);

  const reset = () => {
    setStep(0); setHasIdea(null); setCategory(''); setLevel('');
    setProjectIdea(''); setSelProject(null); setSuggestions([]);
    setForm({ name:'', phone:'', email:'', college:'', description:'' });
    setErrors({}); setGenerating(false); setReport(''); setReportErr('');
  };

  const handleClose = () => { reset(); onClose(); };

  // Step 0 → 1: pick intent
  const pickIntent = (val) => {
    setHasIdea(val);
    goNext();
  };

  // Step 1 → 2: pick category
  const pickCategory = (cat) => {
    setCategory(cat);
    goNext();
  };

  // Step 2 → 3: pick level
  const pickLevel = (lvl) => {
    setLevel(lvl);
    if (!hasIdea) {
      const sugg = getProjectSuggestions(category, lvl, 6);
      setSuggestions(sugg);
    }
    goNext();
  };

  // Step 3 → 4: project selected
  const pickProject = (proj) => {
    setSelProject(proj);
    goNext();
  };

  const handleIdeaSubmit = () => {
    if (!projectIdea.trim()) return;
    setSelProject({ id: 0, title: projectIdea, category, level, suggested_tech_stack: [], description: projectIdea });
    goNext();
  };

  // Step 4 → 5: validate form
  const validateForm = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.phone.trim()) errs.phone = 'Phone is required';
    if (!form.email.trim() || !form.email.includes('@')) errs.email = 'Valid email required';
    if (!form.college.trim()) errs.college = 'College/Organisation is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitForm = async () => {
    if (!validateForm()) return;
    setGenerating(true);
    goNext();

    let generatedReport = '';
    
    try {
      generatedReport = await generateGroqReport({
        title: selProject?.title,
        techStack: selProject?.suggested_tech_stack,
        category,
        level,
        description: form.description || selProject?.description,
        name: form.name,
      });
      setReport(generatedReport);
    } catch (err) {
      console.error(err);
      setReportErr(err.message || 'Failed to process request. Please try again or contact us via WhatsApp.');
    } finally {
      setGenerating(false);
    }

    // Save to Firebase (regardless of AI success)
    try {
      await addDoc(collection(db, 'connect_students'), {
        name: form.name || '',
        phone: form.phone || '',
        email: form.email || '',
        college: form.college || '',
        description: form.description || selProject?.description || '',
        category: category || '',
        level: level || '',
        projectTitle: selProject?.title || projectIdea || '',
        report: generatedReport || 'AI Generation Failed',
        timestamp: new Date().toISOString()
      });
    } catch (e) {
      console.error('Firebase save error:', e);
    }

    // Send Email to Visitor
    try {
      await sendEmail({
        to: form.email,
        subject: "Your AI Project Report - Connect Students",
        text: buildVisitorEmail({ 
          name: form.name || 'Student', 
          email: form.email, 
          projectTitle: selProject?.title || projectIdea || 'Your Project', 
          report: generatedReport || 'Our AI is currently experiencing high demand and could not generate the full report at this moment. However, we have received your project details and our academic expert will reach out to you shortly to provide the complete architecture, timeline, and deliverables.', 
          domain: category || '', 
          level: level || '' 
        })
      });
    } catch (e) {
      console.error('Visitor email send error:', e);
    }

    // Send Email to Admin
    try {
      await sendEmail({
        to: OWNER_EMAIL,
        subject: `New Lead: ${selProject?.title || projectIdea || 'Project'} - Connect Students`,
        text: buildOwnerEmail({ 
          ...form,
          projectTitle: selProject?.title || projectIdea || 'N/A', 
          domain: category || 'N/A', 
          level: level || 'N/A', 
          report: generatedReport || 'AI Generation Failed. Lead captured via Talk To Expert.'
        })
      });
    } catch (e) {
      console.error('Owner email send error:', e);
    }
  };

  const slideVariants = {
    enter: { x: 60, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: -60, opacity: 0 },
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto"
        style={{ background: 'rgba(11,28,78,0.85)', backdropFilter: 'blur(8px)' }}
        onClick={(e) => e.target === e.currentTarget && handleClose()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 40 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 22 }}
          className="relative w-full max-w-3xl mx-4 my-8 rounded-3xl overflow-hidden shadow-2xl"
          style={{ background: '#FAFAF5' }}
        >
          {/* Modal Header */}
          <div className="relative px-8 pt-8 pb-6" style={{ background: 'linear-gradient(135deg, #0B1C4E 0%, #1E3A6E 100%)' }}>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-royal-gold to-transparent" />
            <button onClick={handleClose}
              className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex items-center gap-3 mb-5">
              <img src={logo} alt="CS" className="h-10 w-10 object-contain" />
              <div>
                <p className="font-playfair font-bold text-white text-lg">Connect Students</p>
                <p className="font-inter text-royal-gold text-xs tracking-wider">AI PROJECT ADVISOR</p>
              </div>
            </div>
            <StepBar current={step} total={STEPS.length} labels={STEPS} />
          </div>

          {/* Modal Body */}
          <div className="px-6 sm:px-10 py-8 min-h-[420px]">
            <AnimatePresence mode="wait">
              {/* ── STEP 0: Intent ─────────────────────────────────────────────── */}
              {step === 0 && (
                <motion.div key="step0" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
                  <p className="font-inter text-xs text-royal-gold font-bold uppercase tracking-widest mb-2">Step 1 of 6</p>
                  <h2 className="font-playfair text-3xl font-bold text-royal-navy mb-2">Do you have a project idea?</h2>
                  <p className="font-inter text-slate-royal/70 mb-8 text-sm">Tell us where you stand and we'll guide you to the perfect project with a full AI-generated report.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <motion.button whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}
                      onClick={() => pickIntent(true)}
                      className="group p-6 rounded-2xl border-2 border-royal-navy/20 hover:border-royal-gold bg-white hover:bg-royal-gold-pale text-left transition-all duration-300 shadow-sm hover:shadow-gold">
                      <div className="w-14 h-14 rounded-2xl bg-royal-navy/5 group-hover:bg-royal-gold/20 flex items-center justify-center mb-4 transition-colors text-3xl">💡</div>
                      <h3 className="font-playfair font-bold text-royal-navy text-lg mb-1">Yes, I have an idea</h3>
                      <p className="font-inter text-sm text-slate-royal/60">Tell us your topic and we'll generate a complete project report with architecture & timeline.</p>
                      <div className="mt-4 flex items-center gap-1 text-royal-gold font-inter font-semibold text-xs">
                        Continue <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </div>
                    </motion.button>
                    <motion.button whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}
                      onClick={() => pickIntent(false)}
                      className="group p-6 rounded-2xl border-2 border-royal-navy/20 hover:border-royal-gold bg-white hover:bg-royal-gold-pale text-left transition-all duration-300 shadow-sm hover:shadow-gold">
                      <div className="w-14 h-14 rounded-2xl bg-royal-navy/5 group-hover:bg-royal-gold/20 flex items-center justify-center mb-4 transition-colors text-3xl">🎯</div>
                      <h3 className="font-playfair font-bold text-royal-navy text-lg mb-1">No, suggest me a project</h3>
                      <p className="font-inter text-sm text-slate-royal/60">We'll pick the best projects from our 10,800+ database based on your domain and level.</p>
                      <div className="mt-4 flex items-center gap-1 text-royal-gold font-inter font-semibold text-xs">
                        Get suggestions <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </div>
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {/* ── STEP 1: Domain ─────────────────────────────────────────────── */}
              {step === 1 && (
                <motion.div key="step1" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
                  <p className="font-inter text-xs text-royal-gold font-bold uppercase tracking-widest mb-2">Step 2 of 6</p>
                  <h2 className="font-playfair text-3xl font-bold text-royal-navy mb-2">Select Your Domain</h2>
                  <p className="font-inter text-slate-royal/70 mb-6 text-sm">Choose the technology domain that best matches your project area.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 max-h-72 overflow-y-auto pr-1">
                    {CATEGORIES.map((cat) => (
                      <motion.button key={cat} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                        onClick={() => pickCategory(cat)}
                        className={`p-3 rounded-xl border-2 text-left transition-all duration-200 ${
                          category === cat
                            ? 'border-royal-gold bg-royal-gold-pale shadow-gold'
                            : 'border-parchment-dark bg-white hover:border-royal-gold/60 hover:bg-royal-gold-pale'
                        }`}>
                        <span className="text-2xl block mb-1.5">{CATEGORY_ICONS[cat] || '📂'}</span>
                        <span className="font-inter text-xs font-semibold text-royal-navy leading-tight line-clamp-2">{cat}</span>
                      </motion.button>
                    ))}
                  </div>
                  <button onClick={goBack} className="mt-5 font-inter text-sm text-slate-royal/50 hover:text-royal-navy flex items-center gap-1 transition-colors">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    Back
                  </button>
                </motion.div>
              )}

              {/* ── STEP 2: Level ──────────────────────────────────────────────── */}
              {step === 2 && (
                <motion.div key="step2" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
                  <p className="font-inter text-xs text-royal-gold font-bold uppercase tracking-widest mb-2">Step 3 of 6</p>
                  <h2 className="font-playfair text-3xl font-bold text-royal-navy mb-2">Your Academic Level</h2>
                  <p className="font-inter text-slate-royal/70 mb-6 text-sm">
                    Domain selected: <span className="font-semibold text-royal-navy">{CATEGORY_ICONS[category]} {category}</span>
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {LEVELS.map((lvl) => (
                      <motion.button key={lvl} whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.97 }}
                        onClick={() => pickLevel(lvl)}
                        className="py-5 px-4 rounded-2xl border-2 border-parchment-dark bg-white hover:border-royal-gold hover:bg-royal-gold-pale transition-all duration-200 shadow-sm hover:shadow-gold text-center group">
                        <p className="font-playfair font-bold text-royal-navy text-lg group-hover:text-royal-gold transition-colors">{lvl}</p>
                      </motion.button>
                    ))}
                  </div>
                  <button onClick={goBack} className="mt-6 font-inter text-sm text-slate-royal/50 hover:text-royal-navy flex items-center gap-1 transition-colors">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    Back
                  </button>
                </motion.div>
              )}

              {/* ── STEP 3a: Type Idea ─────────────────────────────────────────── */}
              {step === 3 && hasIdea && (
                <motion.div key="step3a" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
                  <p className="font-inter text-xs text-royal-gold font-bold uppercase tracking-widest mb-2">Step 4 of 6</p>
                  <h2 className="font-playfair text-3xl font-bold text-royal-navy mb-2">Describe Your Project Idea</h2>
                  <p className="font-inter text-slate-royal/70 mb-6 text-sm">Share your project title or idea — our AI will build a complete proposal around it.</p>
                  <div className="space-y-4">
                    <div>
                      <label className="font-inter text-sm font-semibold text-royal-navy block mb-2">Project Title or Idea *</label>
                      <input type="text" value={projectIdea} onChange={(e) => setProjectIdea(e.target.value)}
                        placeholder="e.g. Brain Tumour Detection using CNN and Transfer Learning"
                        className="w-full border-2 border-parchment-dark rounded-xl px-4 py-3.5 font-inter text-sm text-royal-navy focus:outline-none focus:border-royal-gold focus:ring-2 focus:ring-royal-gold/15 transition-all bg-white" />
                    </div>
                    <div className="bg-royal-gold-pale border border-royal-gold/30 rounded-xl p-4 flex gap-3">
                      <span className="text-xl flex-shrink-0">💡</span>
                      <div>
                        <p className="font-inter text-xs font-semibold text-royal-navy mb-0.5">Pro Tip</p>
                        <p className="font-inter text-xs text-slate-royal/70">The more specific your title, the better the AI report. Include your technique + application domain.</p>
                      </div>
                    </div>
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      onClick={handleIdeaSubmit} disabled={!projectIdea.trim()}
                      className="w-full py-4 rounded-xl font-inter font-bold text-base transition-all disabled:opacity-40 text-royal-navy"
                      style={{ background: 'linear-gradient(135deg, #C9A84C, #F0D080)', boxShadow: '0 4px 20px rgba(201,168,76,0.35)' }}>
                      Continue with this idea →
                    </motion.button>
                  </div>
                  <button onClick={goBack} className="mt-4 font-inter text-sm text-slate-royal/50 hover:text-royal-navy flex items-center gap-1 transition-colors">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    Back
                  </button>
                </motion.div>
              )}

              {/* ── STEP 3b: Suggest Projects ──────────────────────────────────── */}
              {step === 3 && !hasIdea && (
                <motion.div key="step3b" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
                  <p className="font-inter text-xs text-royal-gold font-bold uppercase tracking-widest mb-2">Step 4 of 6</p>
                  <h2 className="font-playfair text-3xl font-bold text-royal-navy mb-2">Handpicked Projects for You</h2>
                  <p className="font-inter text-slate-royal/70 mb-5 text-sm">
                    From our database of <span className="font-bold text-royal-navy">10,800+ projects</span> in <span className="font-bold text-royal-navy">{category}</span> for <span className="font-bold text-royal-navy">{level}</span>:
                  </p>
                  <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
                    {suggestions.map((proj, i) => (
                      <motion.button key={proj.id} whileHover={{ scale: 1.01, x: 4 }} whileTap={{ scale: 0.99 }}
                        onClick={() => pickProject(proj)}
                        className="w-full text-left bg-white border-2 border-parchment-dark hover:border-royal-gold hover:shadow-gold rounded-2xl p-4 transition-all duration-200 group flex items-start gap-3">
                        <span className="w-7 h-7 rounded-xl bg-royal-gold/15 text-royal-gold text-xs font-bold flex items-center justify-center flex-shrink-0 group-hover:bg-royal-gold group-hover:text-white transition-colors font-playfair">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-inter font-semibold text-royal-navy text-sm leading-snug group-hover:text-royal-gold transition-colors">{proj.title}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {proj.suggested_tech_stack.slice(0, 4).map((t) => (
                              <span key={t} className="bg-royal-navy/5 text-royal-navy text-[10px] font-semibold px-2 py-0.5 rounded-full">{t}</span>
                            ))}
                            {proj.difficulty && <span className="bg-royal-gold/10 text-royal-gold text-[10px] font-bold px-2 py-0.5 rounded-full">{proj.difficulty}</span>}
                          </div>
                        </div>
                        <svg className="w-4 h-4 text-parchment-dark group-hover:text-royal-gold transition-colors mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </motion.button>
                    ))}
                  </div>
                  <button onClick={goBack} className="mt-4 font-inter text-sm text-slate-royal/50 hover:text-royal-navy flex items-center gap-1 transition-colors">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    Back
                  </button>
                </motion.div>
              )}

              {/* ── STEP 4: User Details Form ──────────────────────────────────── */}
              {step === 4 && (
                <motion.div key="step4" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
                  <p className="font-inter text-xs text-royal-gold font-bold uppercase tracking-widest mb-2">Step 5 of 6</p>
                  <h2 className="font-playfair text-3xl font-bold text-royal-navy mb-1">Your Details</h2>
                  <p className="font-inter text-slate-royal/70 mb-6 text-sm">
                    Project: <span className="font-semibold text-royal-navy">"{selProject?.title?.slice(0, 60)}{selProject?.title?.length > 60 ? '…' : ''}"</span>
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { key:'name',    label:'Full Name *',                       placeholder:'e.g. Rahul Sharma',         type:'text' },
                      { key:'phone',   label:'Phone / WhatsApp *',                placeholder:'e.g. +91 98765 43210',      type:'tel' },
                      { key:'email',   label:'Email Address *',                   placeholder:'e.g. rahul@gmail.com',      type:'email' },
                      { key:'college', label:'College / Organisation *',          placeholder:'e.g. VIT Vellore / TCS',    type:'text' },
                    ].map(({ key, label, placeholder, type }) => (
                      <div key={key}>
                        <label className="font-inter text-sm font-semibold text-royal-navy block mb-1.5">{label}</label>
                        <input type={type} value={form[key]} onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))}
                          placeholder={placeholder}
                          className={`w-full border-2 rounded-xl px-4 py-3 font-inter text-sm focus:outline-none focus:ring-2 focus:ring-royal-gold/15 transition-all bg-white ${
                            errors[key] ? 'border-red-400 focus:border-red-400' : 'border-parchment-dark focus:border-royal-gold'
                          }`} />
                        {errors[key] && <p className="font-inter text-xs text-red-500 mt-1">{errors[key]}</p>}
                      </div>
                    ))}
                    <div className="sm:col-span-2">
                      <label className="font-inter text-sm font-semibold text-royal-navy block mb-1.5">Brief Requirements <span className="text-slate-royal/40 font-normal">(optional)</span></label>
                      <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Any specific requirements, existing data, preferred framework, or deadline..."
                        rows={3}
                        className="w-full border-2 border-parchment-dark rounded-xl px-4 py-3 font-inter text-sm focus:outline-none focus:border-royal-gold focus:ring-2 focus:ring-royal-gold/15 transition-all resize-none bg-white" />
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button onClick={goBack} className="px-6 py-3.5 rounded-xl border-2 border-parchment-dark font-inter font-semibold text-sm text-slate-royal/60 hover:border-royal-navy hover:text-royal-navy transition-colors">
                      Back
                    </button>
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                      onClick={submitForm}
                      className="flex-1 py-3.5 rounded-xl font-inter font-bold text-base text-royal-navy transition-all"
                      style={{ background: 'linear-gradient(135deg, #C9A84C, #F0D080)', boxShadow: '0 4px 20px rgba(201,168,76,0.35)' }}>
                      🤖 Generate AI Project Report
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {/* ── STEP 5: Report ─────────────────────────────────────────────── */}
              {step === 5 && (
                <motion.div key="step5" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
                  {generating ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-6">
                      <div className="relative">
                        <div className="w-20 h-20 rounded-full border-4 border-parchment-dark border-t-royal-gold animate-spin" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg className="w-8 h-8 text-royal-gold" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"/>
                          </svg>
                        </div>
                      </div>
                      <div className="text-center">
                        <h3 className="font-playfair text-2xl font-bold text-royal-navy mb-2">Generating Your AI Report</h3>
                        <p className="font-inter text-slate-royal/60 text-sm">Our AI is crafting your personalized project proposal…</p>
                      </div>
                      <div className="flex flex-col gap-2 w-full max-w-xs">
                        {['Analyzing project requirements', 'Designing system architecture', 'Planning 15-day timeline', 'Curating research paper strategy'].map((t, i) => (
                          <motion.div key={t} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.6 }}
                            className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-royal-gold animate-pulse" />
                            <span className="font-inter text-xs text-slate-royal/60">{t}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ) : reportErr ? (
                    <div className="text-center py-12">
                      <div className="text-5xl mb-4">😢</div>
                      <h3 className="font-playfair text-2xl font-bold text-royal-navy mb-2">Report Generation Failed</h3>
                      <p className="font-inter text-slate-royal/60 text-sm mb-6">{reportErr}</p>
                      <div className="flex justify-center gap-4 mt-6">
                        <a href="https://wa.me/917676654809" target="_blank" rel="noopener noreferrer" className="px-6 py-3 rounded-xl border-2 border-royal-navy font-inter font-bold text-sm text-royal-navy hover:bg-royal-navy hover:text-white transition-colors">WhatsApp Expert</a>
                        <button onClick={() => setStep(0)} className="px-6 py-3 rounded-xl bg-royal-navy text-white font-inter font-bold text-sm hover:bg-royal-navy/90 transition-colors">New Request</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Report header */}
                      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                        <div>
                          <p className="font-inter text-xs text-royal-gold font-bold uppercase tracking-widest mb-1">AI Generated Report</p>
                          <h2 className="font-playfair text-2xl font-bold text-royal-navy">Your Project Proposal</h2>
                        </div>
                        <div className="flex items-center gap-2 bg-green-50 border border-green-200 px-3 py-1.5 rounded-full">
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                          <span className="font-inter text-xs font-semibold text-green-700">Powered by Compound Mini</span>
                        </div>
                      </div>

                      {/* Project summary card */}
                      <div className="bg-royal-navy rounded-2xl p-5 mb-5 relative overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-royal-gold to-transparent" />
                        <div className="grid sm:grid-cols-3 gap-4">
                          <div>
                            <p className="font-inter text-white/50 text-xs uppercase tracking-wider mb-0.5">Project</p>
                            <p className="font-inter font-semibold text-white text-sm leading-snug line-clamp-2">{selProject?.title}</p>
                          </div>
                          <div>
                            <p className="font-inter text-white/50 text-xs uppercase tracking-wider mb-0.5">Student</p>
                            <p className="font-inter font-semibold text-white text-sm">{form.name}</p>
                            <p className="font-inter text-white/50 text-xs">{form.college}</p>
                          </div>
                          <div>
                            <p className="font-inter text-white/50 text-xs uppercase tracking-wider mb-0.5">Delivery</p>
                            <p className="font-playfair font-bold text-royal-gold text-lg">15 Days</p>
                            <p className="font-inter text-white/50 text-xs">Level: {level}</p>
                          </div>
                        </div>
                      </div>

                      {/* Report content */}
                      <div className="bg-white border border-parchment-dark rounded-2xl p-6 max-h-80 overflow-y-auto">
                        <ReportRenderer text={report} />
                      </div>

                      {/* CTA buttons */}
                      <div className="flex gap-4 mt-6">
                        <a href={`https://wa.me/917676654809?text=Hi! I generated a project report for "${selProject?.title?.slice(0,50)}". My name is ${form.name}, phone: ${form.phone}. Please connect me with an expert.`} target="_blank" rel="noopener noreferrer" className="flex-1 bg-[#25D366] hover:bg-[#1DA851] text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm shadow-md">
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          WhatsApp Our Expert
                        </a>
                        <button onClick={reset}
                          className="flex-1 sm:flex-none py-3.5 px-6 rounded-xl font-inter font-bold text-sm text-royal-navy border-2 border-royal-navy/20 hover:border-royal-navy transition-colors">
                          Start New Report
                        </button>
                        <button onClick={handleClose}
                          className="flex-1 sm:flex-none py-3.5 px-6 rounded-xl font-inter font-bold text-sm text-royal-navy"
                          style={{ background: 'linear-gradient(135deg, #C9A84C, #F0D080)' }}>
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer note */}
          {step < 5 && (
            <div className="px-8 py-3 bg-parchment border-t border-parchment-dark flex items-center justify-between">
              <p className="font-inter text-xs text-slate-royal/40">🔒 Your details are 100% secure & private</p>
              <p className="font-inter text-xs text-slate-royal/40">10,800+ Projects Database</p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
